import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const services = [
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="9" x2="9" y2="21"/></svg>
    ),
    title: 'Parking Lot Striping',
    desc: 'Precision re-striping for commercial lots, loading zones, fire lanes, and stall layouts. Fresh lines that maximize capacity and traffic flow.',
    tag: 'Commercial',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
    ),
    title: 'ADA / TAS Compliance',
    desc: 'Fully compliant handicap spaces, van-accessible stalls, access aisles, and ISA signage installed to federal ADA and Texas TAS standards.',
    tag: 'Compliance',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
    ),
    title: 'Pressure Washing',
    desc: 'Commercial-grade high-pressure cleaning for parking lots, building exteriors, sidewalks, dumpster pads, and drive-throughs.',
    tag: 'Commercial & Residential',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>
    ),
    title: 'Soft Wash',
    desc: 'Low-pressure chemical treatment for delicate surfaces — stucco, siding, rooflines — that kills biological growth at the root.',
    tag: 'Residential',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
    ),
    title: 'Junk Removal',
    desc: 'Fast, professional removal of commercial debris, bulk waste, and unwanted items. We haul it — you get a clean, clear property.',
    tag: 'Commercial',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>
    ),
    title: 'Dumpster & Can Cleanup',
    desc: 'Dumpster pad pressure washing and waste area sanitization — keeping commercial properties odor-free, pest-resistant, and code-compliant.',
    tag: 'Commercial',
  },
];

const whyItems = [
  { num: '01', title: 'ADA & TAS Expertise', body: 'We build every job around federal ADA and Texas TAS standards — sign height, van designation, access aisle widths, slope limits. You get a closeout documentation packet on every compliance job.' },
  { num: '02', title: 'Veteran-Owned & SBA Certified', body: 'Pace Plan is an SBA-certified Service-Disabled Veteran-Owned Small Business (SDVOSB) and Veteran-Owned Small Business (VOSB). That commitment to excellence carries into every project.' },
  { num: '03', title: 'Commercial-First Mindset', body: 'We understand that your tenants and customers can\'t wait. Night and weekend scheduling available. Minimal disruption, maximum results — with documentation your property management team can file.' },
  { num: '04', title: 'Clean First. Always.', body: 'We never stripe over a dirty surface. Every striping job begins with a thorough surface evaluation. Paint bonds better, lasts longer, and looks sharper when the prep work is done right.' },
];

const Home: React.FC = () => (
  <div className="home">

    {/* HERO */}
    <section className="hero" aria-label="Hero">
      <div className="container-xl py-5">
        <div className="row align-items-center g-5">
          <div className="col-12 col-lg-6 hero__content">
            <div className="hero__badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              SBA Certified SDVOSB · Serving Greater Houston
            </div>
            <h1 className="hero__title">
              Compliant.<br />
              Clean.<br />
              <em>Commercial-Grade.</em>
            </h1>
            <p className="hero__sub">
              Pace Plan LLC provides ADA/TAS compliance striping, commercial pressure washing, junk removal, and property maintenance for commercial properties and portfolios across the Greater Houston area.
            </p>
            <div className="d-flex flex-wrap gap-3 mt-4">
              <Link to="/contact" className="btn btn-brand-primary">Request a Quote</Link>
              <Link to="/services" className="btn btn-brand-outline-white">View Services</Link>
            </div>
          </div>

          <div className="col-lg-6 d-none d-lg-flex justify-content-center" aria-hidden="true">
            {/* 8-pointed nautical compass rose matching the Pace Plan logo */}
            <svg className="hero__compass" viewBox="0 0 400 400" fill="none">
              {/* Outer decorative rings */}
              <circle cx="200" cy="200" r="190" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5"/>
              <circle cx="200" cy="200" r="175" stroke="rgba(232,58,46,0.2)" strokeWidth="1" strokeDasharray="5 12"/>
              <circle cx="200" cy="200" r="160" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
              <circle cx="200" cy="200" r="55"  stroke="rgba(255,255,255,0.1)"  strokeWidth="1"/>

              {/* Cardinal N — white, brightest */}
              <polygon points="200,22 210,168 200,158 190,168" fill="white" opacity="0.9"/>
              {/* Cardinal S — dim white */}
              <polygon points="200,378 190,232 200,242 210,232" fill="white" opacity="0.38"/>
              {/* Cardinal E — brand red */}
              <polygon points="378,200 232,210 242,200 232,190" fill="#E83A2E" opacity="0.88"/>
              {/* Cardinal W — dim white */}
              <polygon points="22,200 168,190 158,200 168,210" fill="white" opacity="0.38"/>

              {/* Intercardinal NE — accent red */}
              <polygon points="283,117 208,190 204,184 198,190" fill="#E83A2E" opacity="0.52"/>
              {/* Intercardinal SE — dim */}
              <polygon points="283,283 208,210 202,216 208,210" fill="white" opacity="0.22"/>
              {/* Intercardinal SW — dim */}
              <polygon points="117,283 192,210 196,216 192,210" fill="white" opacity="0.22"/>
              {/* Intercardinal NW — dim white */}
              <polygon points="117,117 192,192 198,186 192,192" fill="white" opacity="0.28"/>

              {/* Hub */}
              <circle cx="200" cy="200" r="20" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.18)" strokeWidth="1"/>
              <circle cx="200" cy="200" r="8"  fill="white" opacity="0.88"/>
            </svg>
          </div>
        </div>
      </div>

      {/* CERT STRIP */}
      <div className="hero__certbar">
        <div className="container-xl py-3">
          <div className="d-flex flex-wrap gap-4 align-items-center">
            <span><span className="cert-star">★</span> SBA Certified SDVOSB</span>
            <span className="d-none d-sm-inline hero__cert-divider" aria-hidden="true"/>
            <span><span className="cert-star">★</span> SBA Certified VOSB</span>
            <span className="d-none d-sm-inline hero__cert-divider" aria-hidden="true"/>
            <span><span className="cert-star">✓</span> ADA / TAS Compliant Work</span>
            <span className="d-none d-sm-inline hero__cert-divider" aria-hidden="true"/>
            <span><span className="cert-star">✓</span> Licensed &amp; Insured · Texas</span>
          </div>
        </div>
      </div>
    </section>

    {/* SERVICES PREVIEW */}
    <section className="section" id="services-preview">
      <div className="container-xl">
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-5">
          <div>
            <span className="section-label">What We Do</span>
            <h2 className="section-title section-title--navy">Commercial Services</h2>
          </div>
          <Link to="/services" className="btn btn-brand-navy">All Services →</Link>
        </div>

        <div className="row g-1 services-grid">
          {services.map((s, i) => (
            <div className="col-12 col-md-6 col-lg-4" key={i}>
              <div className="service-card">
                <div className="service-card__icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <span className="tag tag--red-border">{s.tag}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <div className="red-bar" />

    {/* WHY PACE PLAN */}
    <section className="section section--dark">
      <div className="container-xl">
        <div className="row g-5 align-items-start">
          <div className="col-12 col-lg-5 why__left">
            <span className="section-label">Why Pace Plan</span>
            <h2 className="section-title section-title--white">
              We Don't Just<br />Paint Lines.<br />
              <span style={{ color: 'var(--red)' }}>We Deliver Results.</span>
            </h2>
            <p className="section-sub section-sub--white" style={{ marginBottom: '2rem' }}>
              Every commercial property we touch gets the same treatment: expert execution, ADA/TAS compliance built in, and documentation your team can use.
            </p>
            <Link to="/about" className="btn btn-brand-primary">About Our Team →</Link>
          </div>
          <div className="col-12 col-lg-7">
            <div className="d-flex flex-column gap-4">
              {whyItems.map((w, i) => (
                <div className="why-pillar d-flex gap-3 align-items-start" key={i}>
                  <span className="why-pillar__num">{w.num}</span>
                  <div>
                    <h4>{w.title}</h4>
                    <p>{w.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* PORTFOLIO TEASER */}
    <section className="section section--gray">
      <div className="container-xl">
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-5">
          <div>
            <span className="section-label">Our Work</span>
            <h2 className="section-title section-title--navy">Recent Projects</h2>
          </div>
          <Link to="/portfolio" className="btn btn-brand-navy">Full Portfolio →</Link>
        </div>
        <div className="row g-4">
          {[
            { tag: 'Commercial · Striping + ADA', title: 'Executive Tint', loc: 'Heights, Houston', desc: 'Full re-stripe with ADA-compliant handicap spaces, van-accessible stall, access aisles, and designated offloading zone.', img: '/images/portfolio/executive-tint-after.jpg' },
            { tag: 'Commercial · Wash + Stripe', title: 'Westpark Strip Mall', loc: 'Westpark, Houston', desc: 'Outdoor strip mall full pressure wash and complete re-stripe — multiple ADA spaces, two offloading zones, all stalls.', img: '/images/portfolio/westpark-strip-mall-after.jpg' },
            { tag: 'Residential · Power Wash', title: "Edy's Estate", loc: 'Greater Houston', desc: '5,100+ sq ft power wash across a sprawling driveway, statement roundabout, and full pool area restoration.', img: '/images/portfolio/edys-estate-after-pool.jpg' },
          ].map((p, i) => (
            <div className="col-12 col-md-6 col-lg-4" key={i}>
              <div className="portfolio-teaser__card">
                <div className="portfolio-teaser__img">
                  <img src={p.img} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <span className="tag tag--navy" style={{ position: 'absolute', top: 12, left: 12 }}>{p.tag}</span>
                </div>
                <div className="portfolio-teaser__body">
                  <h3>{p.title}</h3>
                  <span className="portfolio-teaser__loc">📍 {p.loc}</span>
                  <p>{p.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA BANNER */}
    <section className="cta-banner">
      <div className="container-xl">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
          <div>
            <h2>Ready to get your property compliant and clean?</h2>
            <p>Free on-site assessment. No obligation. Quotes within 24 hours.</p>
          </div>
          <div className="d-flex flex-wrap gap-3">
            <Link to="/contact" className="btn btn-brand-primary">Request a Free Quote</Link>
            <a href="tel:3464194499" className="btn btn-brand-outline-white">(346) 419-4499</a>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Home;
